/**
 * Flow-Engine: zentraler chatFlowState für Wizard-in-Chat.
 * State: { active, flowId, stepId, answers (payload), draft, meta }.
 * Regeln: Bei active === true gehört jede User-Aktion dem Wizard (kein Intent, keine Vorschläge).
 */

import { getFlow } from "./flow.registry.js";

/** Zentraler Flow-State (chatFlowState): active, flowId, stepId, payload/answers */
export function getInitialState() {
  return {
    active: false,
    flowId: null,
    stepId: null,
    answers: {},
    draft: {},
    meta: {},
  };
}

/** Wiederholungs-Schutz: derselbe Flow wird nicht erneut gestartet, solange er aktiv ist */
export function startFlow(state, flowId) {
  if (state.active && state.flowId === flowId) {
    return { state: { ...state }, assistantTextKey: "", quickReplies: [] };
  }
  const flow = getFlow(flowId);
  if (!flow) {
    return { state: { ...state }, assistantTextKey: "", quickReplies: [] };
  }
  const stepId = flow.startStepId;
  const step = flow.steps[stepId];
  if (!step) {
    return { state: { ...state }, assistantTextKey: "", quickReplies: [] };
  }
  const now = Date.now();
  const newState = {
    active: true,
    flowId,
    stepId,
    answers: {},
    draft: {},
    meta: { startedAt: now, lastUpdatedAt: now },
  };
  const quickReplies = (step.quickReplies || []).map((qr, i) => ({
    id: "qr-" + flowId + "-" + stepId + "-" + i,
    labelKey: qr.key,
    value: qr.value,
  }));
  return {
    state: newState,
    assistantTextKey: step.messageKey,
    assistantTextParams: step.messageParams,
    quickReplies,
    toolCalls: undefined,
  };
}

/** Wizard-Ende: active = false, normale Chat-Vorschläge wieder erlaubt */
export function cancelFlow(state) {
  return getInitialState();
}

export function handleUserMessage(state, text) {
  if (!state.active || !state.flowId || !state.stepId) {
    return { state: { ...state }, action: "api" };
  }
  const flow = getFlow(state.flowId);
  if (!flow) return { state: cancelFlow(state), action: "api" };
  const step = flow.steps[state.stepId];
  if (!step) return { state: cancelFlow(state), action: "api" };

  const normalized = String(text).trim().toLowerCase();
  const list = step.quickReplies || [];
  const valueFromChip = list.find((qr) => qr.value.toLowerCase() === normalized);
  const answer = valueFromChip ? valueFromChip.value : text;

  if (answer === "__cancel") {
    return {
      state: cancelFlow(state),
      action: "cancel",
      assistantTextKey: undefined,
      quickReplies: undefined,
    };
  }

  const nextStepId = step.apply(answer);
  const now = Date.now();
  const newAnswers = { ...state.answers, [state.stepId]: answer };
  const newDraft = { ...state.draft };
  if (state.stepId === "mode") newDraft.mode = answer;
  if (state.stepId === "players") newDraft.playersCount = answer;

  if (nextStepId == null || nextStepId === "") {
    return { state: cancelFlow(state), action: "assistant", assistantTextKey: undefined, quickReplies: [] };
  }
  const nextStep = flow.steps[nextStepId];
  if (!nextStep) {
    const nextState = {
      ...state,
      stepId: nextStepId,
      answers: newAnswers,
      draft: newDraft,
      meta: { ...state.meta, lastUpdatedAt: now },
    };
    return { state: nextState, action: "assistant", assistantTextKey: undefined, quickReplies: [] };
  }

  const quickReplies = (nextStep.quickReplies || []).map((qr, i) => ({
    id: "qr-" + state.flowId + "-" + nextStepId + "-" + i,
    labelKey: qr.key,
    value: qr.value,
  }));

  let toolCalls;
  if (nextStepId === "review" && nextStep.maybeFinalize) {
    const fin = nextStep.maybeFinalize();
    if (fin && fin.toolCall) toolCalls = [fin.toolCall];
  }

  const newState = {
    ...state,
    active: true,
    stepId: nextStepId,
    answers: newAnswers,
    draft: newDraft,
    meta: { ...state.meta, lastUpdatedAt: now },
  };

  return {
    state: newState,
    action: "assistant",
    assistantTextKey: nextStep.messageKey,
    assistantTextParams: nextStep.messageParams,
    quickReplies,
    toolCalls,
  };
}

export function applyToolResult(state) {
  return { ...state };
}
